<template>
  <BittsLayout :is-page="true" class="sharing-settings-page">
    <div
      :class="{ 'sharing-settings-page__padded': isSharingSettingsPopulation }"
    >
      <RouterView />
    </div>
    <div v-if="!isSharingSettingsPopulation">
      <div
        class="sharing-settings-page__header"
        :class="{
          'sharing-settings-page__header--flush': isShowingPartnersTab,
        }"
      >
        <PageTitle
          title="Sharing Settings"
          subtitle="Manage sharing settings for all your Populations and Partners — view defaults, customize sharing, and apply changes in bulk"
          class="mb-20"
        >
          <template #action>
            <div class="flex items-center gap-8">
              <BittsButton
                v-if="hasPartnerTrackerAccess"
                text="Requests"
                type="neutral"
                variant="outline"
                size="small"
                class="text-14"
                :left-icon="['far', 'inbox']"
                left-icon-classes="pr-8"
                data-testid="open-requests-drawer"
                @click="handleOpenRequests"
              >
                <template #text-extra>
                  <BittsCount
                    v-if="requestsDrawerRef?.badgeCount"
                    :count="requestsDrawerRef?.badgeCount ?? 0"
                    class="ml-8"
                  />
                </template>
              </BittsButton>
              <BittsButton
                v-if="isSaveVisible"
                text="Save"
                :disabled="!hasChanges"
                :loading="isSaving"
                @click="handleSave"
                size="small"
              />
            </div>
          </template>
        </PageTitle>
        <SharingSettingsByPopulationsTab
          v-if="!isShowingPartnersTab"
          ref="populationsTabRef"
          :hide-title="hasByPartnersAccess"
        >
          <template v-if="hasByPartnersAccess" #prepend>
            <BittsSegment
              :tabs="segmentTabs"
              :active-index="activeSegmentIndex"
              @change:segment="onSegmentChange"
            />
          </template>
        </SharingSettingsByPopulationsTab>
      </div>
      <SharingSettingsByPartnersTab
        v-if="isShowingPartnersTab"
        ref="partnersTabRef"
        :can-manage-partners="canManagePartners"
        @open-partner-settings="openPartnerSettings"
      >
        <template v-if="hasByPartnersAccess" #prepend>
          <BittsSegment
            :tabs="segmentTabs"
            :active-index="activeSegmentIndex"
            class="w-fit"
            @change:segment="onSegmentChange"
          />
        </template>
        <template #actions>
          <PartnerTabBulkActions
            v-if="canManagePartners"
            :selected-count="partnersSelectedCount"
            @edit-tags="openEditTagsModal"
            @remove="openRemovePartnersModal"
          />
        </template>
      </SharingSettingsByPartnersTab>
    </div>
  </BittsLayout>
  <RequestsDrawer
    :key="drawerCloseRouteName"
    v-if="hasPartnerTrackerAccess"
    ref="requestsDrawerRef"
    open-route-name="sharing_settings_requests"
    :close-route-name="drawerCloseRouteName"
    accept-route-name="sharing_settings_requests_accept_proposal"
    default-view="data_share"
    :can-edit-sharing-in-place="hasByPartnersAccess"
    @edit-sharing="editSharing"
  />
  <PartnershipSettingsModal
    v-if="partnerSettingsTarget"
    :partner-id="partnerSettingsTarget.partnerId"
    :initial-tab="partnerSettingsTarget.tab"
    :initial-segment="partnerSettingsTarget.segment"
    @close="closePartnerSettings"
  />
  <PartnerTabEditTagsModal
    v-if="editTagsPartners"
    :partners="editTagsPartners"
    :is-saving="isSavingTags"
    @save="handleSaveTags"
    @closed="closeEditTagsModal"
  />
  <PartnerTabRemovePartnersModal
    v-if="partnersToRemove"
    :partners="partnersToRemove"
    :is-removing="isRemovingPartners"
    @remove="handleRemovePartners"
    @closed="closeRemovePartnersModal"
  />
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCount,
  BittsLayout,
  BittsSegment,
} from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed, ref, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import RequestsDrawer from '@/components/dash/RequestsDrawer.vue';
import PageTitle from '@/components/PageTitle.vue';
import type { TagEditType } from '@/components/partners/bulkTagEdit';
import { PartnershipSettingsTabType } from '@/components/partners/DataSharingSettings/SettingsModal/constants';
import PartnershipSettingsModal from '@/components/partners/DataSharingSettings/SettingsModal/PartnershipSettingsModal.vue';
import PartnerTabBulkActions from '@/components/sharing-settings/partner-tab/components/PartnerTabBulkActions.vue';
import PartnerTabEditTagsModal from '@/components/sharing-settings/partner-tab/components/PartnerTabEditTagsModal.vue';
import PartnerTabRemovePartnersModal from '@/components/sharing-settings/partner-tab/components/PartnerTabRemovePartnersModal.vue';
import { removeBulkPartners } from '@/components/sharing-settings/partner-tab/removeBulkPartners';
import { saveBulkTagEdit } from '@/components/sharing-settings/partner-tab/saveBulkTagEdit';
import SharingSettingsByPartnersTab from '@/components/sharing-settings/partner-tab/SharingSettingsByPartnersTab.vue';
import type {
  PartnerSettingsTargetType,
  SharingPartnerRow,
} from '@/components/sharing-settings/partner-tab/types';
import SharingSettingsByPopulationsTab from '@/components/sharing-settings/population-tab/SharingSettingsByPopulationsTab.vue';

import useAuth from '@/composables/useAuth';
import useDataSharingPermissions from '@/composables/useDataSharingPermissions';
import {
  TEMP_PARTNER_TRACKER_ACCESS,
  TEMP_SHARING_SETTINGS_BY_PARTNERS,
} from '@/constants/feature_flags';
import { useFeatureFlagStore, usePopulationsStore } from '@/stores';
import type { InboundShareRequest } from '@/types/data_share_requests';

useHead({
  title: 'Sharing Settings - Crossbeam',
});

const route = useRoute();
const router = useRouter();
const { hasFeatureFlag } = useFeatureFlagStore();
const populationsStore = usePopulationsStore();

const hasPartnerTrackerAccess = computed(() =>
  hasFeatureFlag(TEMP_PARTNER_TRACKER_ACCESS),
);

const hasByPartnersAccess = computed(() =>
  hasFeatureFlag(TEMP_SHARING_SETTINGS_BY_PARTNERS),
);

const isByPartnersTab = computed(
  () =>
    route.name === 'sharing-settings-by-partners' ||
    route.name === 'sharing_settings_edit_partner_tags',
);

const isOnRequestsRoute = computed(
  () =>
    route.name === 'sharing_settings_requests' ||
    route.name === 'sharing_settings_requests_accept_proposal',
);

const wasOnPartnersTab = ref(false);

const isShowingPartnersTab = computed(() => {
  if (isOnRequestsRoute.value) return wasOnPartnersTab.value;
  return isByPartnersTab.value;
});

const isSharingSettingsPopulation = computed(
  () => route.name === 'sharing-settings-by-population',
);

/* ---- Segment tabs ---- */

const SEGMENT_BY_POPULATIONS = 'by-populations';
const SEGMENT_BY_PARTNERS = 'by-partners';

const segmentTabs = [
  { id: SEGMENT_BY_POPULATIONS, title: 'By Populations' },
  { id: SEGMENT_BY_PARTNERS, title: 'By Partners' },
];

const activeSegmentIndex = computed(() => (isShowingPartnersTab.value ? 1 : 0));

function onSegmentChange(id: string | number) {
  if (id === SEGMENT_BY_PARTNERS) {
    router.push({ name: 'sharing-settings-by-partners' });
  } else {
    router.push({ name: 'sharing-settings-by-populations' });
  }
}

const drawerCloseRouteName = computed(() => {
  if (isShowingPartnersTab.value) return 'sharing-settings-by-partners';
  if (hasByPartnersAccess.value) return 'sharing-settings-by-populations';
  return 'sharing-settings';
});

function handleOpenRequests() {
  wasOnPartnersTab.value = isByPartnersTab.value;
  requestsDrawerRef.value?.open();
}

/* ---- Partner settings modal ---- */

const partnerSettingsTarget = shallowRef<PartnerSettingsTargetType | null>(
  null,
);

function openPartnerSettings(target: PartnerSettingsTargetType) {
  partnerSettingsTarget.value = target;
}

function closePartnerSettings() {
  partnerSettingsTarget.value = null;
}

/** Every Sharing Settings route reuses this page, so browser Back would otherwise leave the modal open. */
watch(() => route.name, closePartnerSettings);

/** Opens over the Requests drawer, filtered to the requested population's segment. */
function editSharing(request: InboundShareRequest) {
  const population = populationsStore.getPopulationById(request.population_id);
  openPartnerSettings({
    partnerId: request.partner_org_id,
    tab: PartnershipSettingsTabType.SHARING_SETTINGS,
    segment: population?.segment ?? undefined,
  });
}

/* ---- Tab refs ---- */

const requestsDrawerRef = ref<{ badgeCount: number; open: () => void } | null>(
  null,
);

const populationsTabRef = ref<InstanceType<
  typeof SharingSettingsByPopulationsTab
> | null>(null);

const partnersTabRef = ref<InstanceType<
  typeof SharingSettingsByPartnersTab
> | null>(null);

const hasChanges = computed(() => {
  if (isShowingPartnersTab.value)
    return partnersTabRef.value?.hasAnyChanges ?? false;
  return populationsTabRef.value?.hasAnyChanges ?? false;
});

const partnersSelectedCount = computed(
  () => partnersTabRef.value?.selectedRows?.length ?? 0,
);

const isSaving = computed(() => {
  if (isShowingPartnersTab.value)
    return partnersTabRef.value?.isSaving ?? false;
  return populationsTabRef.value?.isSaving ?? false;
});

const editTagsPartners = shallowRef<SharingPartnerRow[] | null>(null);

function openEditTagsModal() {
  editTagsPartners.value = [...(partnersTabRef.value?.selectedRows ?? [])];
}

function closeEditTagsModal() {
  editTagsPartners.value = null;
}

const isSavingTags = ref(false);

async function handleSaveTags(edit: TagEditType) {
  isSavingTags.value = true;
  try {
    const isSaved = await saveBulkTagEdit(editTagsPartners.value ?? [], edit);
    if (!isSaved) return;
    partnersTabRef.value?.clearSelection();
    closeEditTagsModal();
  } finally {
    isSavingTags.value = false;
  }
}

const partnersToRemove = shallowRef<SharingPartnerRow[] | null>(null);

function openRemovePartnersModal() {
  partnersToRemove.value = [...(partnersTabRef.value?.selectedRows ?? [])];
}

function closeRemovePartnersModal() {
  partnersToRemove.value = null;
}

const isRemovingPartners = ref(false);

async function handleRemovePartners() {
  isRemovingPartners.value = true;
  try {
    const isRemoved = await removeBulkPartners(partnersToRemove.value ?? []);
    if (isRemoved) closeRemovePartnersModal();
  } finally {
    isRemovingPartners.value = false;
  }
}

const { canEdit } = useDataSharingPermissions();

const { hasScope } = useAuth();
const canManagePartners = computed(() => hasScope('write:partnerships'));

const isSaveVisible = computed(
  () =>
    !isShowingPartnersTab.value &&
    !!populationsTabRef.value?.hasPopulations &&
    canEdit.value,
);

async function handleSave() {
  if (isShowingPartnersTab.value) {
    await partnersTabRef.value?.saveChanges();
  } else {
    await populationsTabRef.value?.saveChanges();
  }
}
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";

/*
 * Remove BittsLayout page padding so the By Partners table can go edge-to-edge.
 * Every other region re-adds it, since the layout no longer supplies any.
 */
.sharing-settings-page.bitts-layout {
  @apply !p-0;
}

.sharing-settings-page__header {
  @apply px-24 pt-24 pb-12;
}

/* The By Partners toolbar carries its own spacing, so the header must not add
   a second gap above it. */
.sharing-settings-page__header--flush {
  @apply pb-0;
}

.sharing-settings-page__padded {
  @apply px-24 py-24;
}
</style>
